// The step-by-step guide, one per role: what to click, in the order the work happens.
//
// WHERE THE PICTURES COME FROM. Each `img` is a real ProTrack screen, taken by
// backend/scripts/guide_shots/ against a throwaway database of invented people and projects
// (Sam Rivera, Taylor Brooks and so on — nobody real). The red numbered marks are drawn on the
// real buttons, so a picture only ever shows a button ProTrack actually renders in that state.
//
// The marks are part of the picture. If you change a step's points, re-take its picture, and if a
// screen changes, re-take the pictures and re-read the points: the numbers must still match.
//
// Every statement here was checked against the code, like rolesContent.js. Two that surprise
// people: a project goes live as soon as a PM creates it (no Admin approval), and Finance pays
// invoices but does not review bills — the PM or an Admin does.

// Which guides a person sees: their own role's, and nobody else's. An Admin (or Super Admin) sees
// all four, because they are the ones who explain ProTrack to everybody else.
export function guidesFor(role, isSuperAdmin = false) {
  if (role === 'Admin' || isSuperAdmin) return GUIDES
  return GUIDES.filter(g => g.role === role)
}

export const GUIDES = [
  {
    key: 'pm',
    role: 'Project Manager',
    label: 'Project Manager',
    intro: 'You run the projects. You create a project, offer its milestones to consultants, approve what they choose, review what they deliver, approve their invoices, and close the project when everything is paid. A project goes live as soon as you create it; money always needs a second approval, from an Admin.',
    steps: [
      {
        title: 'Your dashboard',
        lead: 'What needs you today, and a way to start something new.',
        img: 'pm-dashboard.webp',
        alt: 'Project Manager dashboard with a Ready to close card, recent projects and quick actions',
        points: [
          { label: 'Ready to close', text: 'a project whose milestones are all done and paid shows here.' },
          { label: 'Close it', text: 'opens the closure page for that project.' },
          { label: 'Recent Projects', text: 'your latest projects and how far along each one is.' },
          { label: 'New Project', text: 'starts a new one.' },
        ],
      },
      {
        title: 'Your projects',
        lead: 'Every project you manage. Each card says what it is waiting for, and who acts next.',
        img: 'pm-projects.webp',
        alt: 'Project list as cards, each with a coloured note saying what it is waiting for',
        points: [
          { label: 'New', text: 'starts a project.' },
          { text: 'The coloured note on a card says what the project is waiting for. "Next: PM" means you.' },
          { label: 'View details', text: 'opens the project.' },
        ],
      },
      {
        title: 'Start a project',
        lead: 'Two ways in. Both open the same form.',
        img: 'pm-new-project.webp',
        alt: 'Start a new project: from scratch, or from a template',
        points: [
          { label: 'Start from scratch', text: 'an empty form.' },
          { label: 'Start from a template', text: 'the stages this kind of work usually goes through, written once under Templates. You still set the dates and the money.' },
        ],
      },
      {
        title: 'Fill in the four steps',
        lead: 'Details, Schedule, Budget & Files, Assign. Nothing is created until you press Create Project on the last step; Save as draft keeps it for later.',
        img: 'pm-create-form.webp',
        alt: 'Create New Project form with its four steps and the Terms of Reference quick start',
        points: [
          { text: 'The four steps. Next moves on, Previous goes back. Milestones are added in Budget & Files.' },
          { label: 'Quick Start', text: 'drop in a Terms of Reference and the form fills itself in, milestones included. Check what it filled.' },
          { label: 'Project Name', text: 'the one thing you must type on this step.' },
        ],
        note: 'The project goes live as soon as you create it. It does not wait for an Admin.',
      },
      {
        title: 'Offer milestones to a consultant',
        lead: 'Open the project, Team tab. Each consultant gets the milestones you pick for them.',
        img: 'pm-offer.webp',
        alt: 'Team tab: choose a consultant, tick milestones, offer them',
        points: [
          { text: 'Choose the consultant. Only consultants whose onboarding is complete can be offered work.' },
          { text: 'Tick the milestones for them. Leave all unticked to offer every open milestone.' },
          { label: 'Offer', text: 'sends the offer. The consultant is told.' },
          { label: 'Consultants on this Project', text: 'who has been offered what, and who has chosen.' },
        ],
      },
      {
        title: 'Approve what they chose',
        lead: 'Project, Overview tab. When a consultant accepts milestones, you approve, and their contract is issued.',
        img: 'pm-approve-selection.webp',
        alt: 'Green box listing the milestones a consultant selected, with Approve & Generate Contract',
        points: [
          { label: '+ Add Milestone', text: 'adds a milestone to the project at any time.' },
          { text: 'The green box lists what the consultant selected.' },
          { label: 'Approve & Generate Contract', text: 'issues their contract. They sign it, then an Admin counter-signs.' },
        ],
      },
      {
        title: 'Review deliverables and bills',
        lead: 'Project, Deliverables tab. Anything waiting for you is marked.',
        img: 'pm-review.webp',
        alt: 'A submitted deliverable with two bills waiting for a decision and a Review button',
        points: [
          { label: 'Review deliverable', text: 'marks a milestone with work waiting.' },
          { text: 'Bills come first: approve (✓), send back (↩) or reject (✕) each one. Every bill needs a decision before the deliverable can be approved.' },
          { label: 'Review', text: 'opens the deliverable: Approve, Request Revision or Reject, with a note.' },
        ],
      },
      {
        title: 'Approve the invoice',
        lead: 'Once the consultant has confirmed their invoice, it needs your approval and an Admin\'s, in either order.',
        img: 'pm-invoice.webp',
        alt: 'Milestone marked Approve invoice, with View Invoice and Approve Invoice buttons',
        points: [
          { label: 'Approve invoice', text: 'marks a milestone waiting for you.' },
          { label: 'View Invoice', text: 'shows the invoice and any bills on it.' },
          { label: 'Approve Invoice → Release to Finance', text: 'gives your approval. When both are in, Finance can pay.' },
        ],
      },
      {
        title: 'Close the project',
        lead: 'Project, Closure button. Three checks must be green first.',
        img: 'pm-closure.webp',
        alt: 'Project Closure page with three green checks and Initiate Closure Request',
        points: [
          { text: 'The checks: every deliverable approved, an active contract, every milestone paid.' },
          { label: 'Initiate Closure Request', text: 'sends it to an Admin, who confirms the closure.' },
        ],
      },
    ],
  },
  {
    key: 'consultant',
    role: 'Consultant',
    label: 'Consultant',
    intro: 'You do the work. You finish your onboarding, accept the milestones you are offered, sign your contract, submit your work and bills, and confirm your invoice. You see only your own projects, contracts and payments.',
    steps: [
      {
        title: 'Your dashboard',
        lead: 'Where you land after signing in.',
        img: 'con-dashboard.webp',
        alt: 'Consultant dashboard with the active contract and the menu',
        points: [
          { label: 'Active Contract', text: 'your current contract and its fee. View opens it.' },
          { label: 'My Projects', text: 'your projects and what each one is waiting for.' },
          { label: 'My Contracts', text: 'every contract, to read or sign.' },
          { label: 'My Onboarding', text: 'your onboarding steps.' },
        ],
      },
      {
        title: 'Finish your onboarding',
        lead: 'Three steps. Do them first.',
        img: 'con-onboarding.webp',
        alt: 'My Onboarding with three steps and Start buttons',
        points: [
          { text: 'How many steps are done.' },
          { label: 'Start', text: 'opens a step: your identity documents, then your consent.' },
          { label: 'Personal details check', text: 'an Admin or HR does this after you submit your documents. There is nothing for you to click.' },
        ],
        note: 'When all three are done, an Admin checks your documents and activates your account. Only then can you be offered work.',
      },
      {
        title: 'Accept the milestones offered to you',
        lead: 'Open the project from My Projects. Milestones offered to you are at the top of the Overview tab.',
        img: 'con-accept.webp',
        alt: 'Milestones offered to you, each with Accept, Reject and a chat',
        points: [
          { text: 'The milestones offered to you, with the amount and due date.' },
          { label: 'Accept', text: 'the ones you will deliver.' },
          { label: 'Reject', text: 'with a reason. The manager sees it.' },
          { label: 'Milestone Chat', text: 'ask the manager about a milestone before you decide.' },
        ],
        note: 'Your contract is issued once the manager approves what you accepted.',
      },
      {
        title: 'Sign your contract',
        lead: 'My Contracts, open the contract. Read it, check your bank details, then sign.',
        img: 'con-contract.webp',
        alt: 'Contract signing: confirm bank details, agree, type your name, Sign Contract',
        points: [
          { text: 'Tick to confirm the bank details shown are right. If they are not, use Update bank details first.' },
          { text: 'Tick to agree to the terms.' },
          { text: 'Type your full name. That is your signature.' },
          { label: 'Sign Contract', text: 'then an Admin counter-signs and the contract is active.' },
        ],
        note: 'Prefer paper? The Upload Signed Copy tab takes a signed scan instead.',
      },
      {
        title: 'Your projects',
        lead: 'My Projects: what you have earned, and what is waiting.',
        img: 'con-projects.webp',
        alt: 'My Consulting Projects with totals and one card per project',
        points: [
          { label: 'My total value', text: 'everything in your milestones.' },
          { label: 'Earnings to date', text: 'what has been paid to you.' },
          { label: 'Awaiting action', text: 'work you submitted that the manager has not reviewed yet.' },
          { label: 'View Project', text: 'opens the project.' },
        ],
      },
      {
        title: 'Submit your work and bills',
        lead: 'Project, Deliverables tab, Submit Deliverables & Bills. This opens once your contract is active.',
        img: 'con-submit.webp',
        alt: 'Submit a Deliverable form with milestone, title, files and bills',
        points: [
          { text: 'Pick the milestone.' },
          { text: 'Give the work a title.' },
          { text: 'Attach the file or files.' },
          { label: 'Attach bill(s)', text: 'for a milestone paid on actual costs: pick the kind of expense, then attach the receipts.' },
          { label: 'Submit', text: 'with bills attached you check the claim total before it is sent.' },
        ],
      },
      {
        title: 'Confirm your invoice',
        lead: 'When the manager has approved your work, the milestone asks you to confirm the invoice.',
        img: 'con-invoice.webp',
        alt: 'Milestone marked Confirm invoice required, with a Confirm Invoice button',
        points: [
          { label: 'Confirm invoice required', text: 'marks the milestone.' },
          { label: 'Confirm Invoice', text: 'opens it. If you see Generate & Submit Invoice instead, that raises the invoice yourself.' },
        ],
      },
      {
        title: 'Check and sign it',
        lead: 'The last step on your side. Then the PM and an Admin approve it, and Finance pays.',
        img: 'con-invoice-sign.webp',
        alt: 'Confirm & Sign Invoice with bank details and a signature box',
        points: [
          { text: 'Where the money will go. If it is wrong, update your bank details first.' },
          { text: 'Type your full name to sign.' },
          { label: 'Confirm & Sign Invoice', text: 'sends it. You are told when it is paid.' },
        ],
      },
    ],
  },
  {
    key: 'finance',
    role: 'Finance',
    label: 'Finance',
    intro: 'You pay. An invoice reaches you only after the consultant has confirmed it and both the PM and an Admin have approved it. You also follow budgets and spending across the projects.',
    steps: [
      {
        title: 'Your dashboard',
        lead: 'Money across every project, at a glance.',
        img: 'fin-dashboard.webp',
        alt: 'Finance dashboard with budget utilisation and pending payments',
        points: [
          { label: 'Budget Utilization', text: 'how much of all approved budget has been paid out.' },
          { label: 'Pending Payments', text: 'approved invoices waiting to be paid.' },
          { label: 'Invoice Queue', text: 'where you pay them.' },
        ],
      },
      {
        title: 'Pay an invoice',
        lead: 'Invoice Queue. Every invoice here already has both approvals.',
        img: 'fin-queue.webp',
        alt: 'Invoice Payment Queue with one invoice and the payment history',
        points: [
          { text: 'One card per invoice: milestone, project, when the consultant confirmed it, and the amount.' },
          { label: 'View Invoice', text: 'shows the full invoice before you pay.' },
          { label: 'Pay & Approve', text: 'records the payment. It asks once more, because it cannot be undone. The consultant and the PM are told.' },
          { label: 'Payment History', text: 'every invoice already paid.' },
        ],
        note: 'You see the invoices of projects whose team you are on; if a project has no Finance person on its team, any Finance user can pay it. An invoice still unpaid 7 days after its approvals is flagged to you every day.',
      },
      {
        title: 'Read the invoice',
        lead: 'What the consultant signed, laid out for payment.',
        img: 'fin-invoice.webp',
        alt: 'Invoice Details: who is billing, the total due and the charges',
        points: [
          { text: 'Who is billing, and where the money goes.' },
          { text: 'The project, the milestone and the total due.' },
          { label: 'Charges', text: 'the fee and any bills, line by line.' },
          { label: 'Print', text: 'for your records.' },
        ],
      },
      {
        title: 'Track budgets',
        lead: 'Budget Tracking: each project\'s approved budget against what has been paid.',
        img: 'fin-budget.webp',
        alt: 'Budget Tracking with totals and a row per project',
        points: [
          { label: 'Overall Utilization', text: 'across all projects.' },
          { text: 'The totals: allocated, spent, remaining, and active projects.' },
          { label: 'Project Budget details', text: 'one row per project, from Low Usage up to Critical.' },
          { label: 'Print / Export', text: 'prints the page or saves it as a PDF.' },
        ],
        note: 'Projects that have used 80% or more of their budget are listed under Budget Alerts, further down the page.',
      },
      {
        title: 'Financial reports',
        lead: 'Financial Reports: the whole portfolio in one place.',
        img: 'fin-reports.webp',
        alt: 'Financial Reports with totals and a project breakdown',
        points: [
          { label: 'Utilization Rate', text: 'spent against allocated, with the other totals beside it.' },
          { label: 'Project Budget Breakdown', text: 'one row per project.' },
          { label: 'Print / Export PDF', text: 'prints the report or saves it as a PDF.' },
        ],
      },
    ],
  },
  {
    key: 'admin',
    role: 'Admin',
    label: 'Admin',
    intro: 'You run the company\'s side. You let people in, counter-sign contracts, give the second approval on every invoice, close and archive projects, and decide who gets extra powers. You can see everything.',
    steps: [
      {
        title: 'Your dashboard',
        lead: 'What is waiting for you, and the menus only an Admin has.',
        img: 'adm-dashboard.webp',
        alt: 'Admin dashboard with a Ready to close card and the Admin menu',
        points: [
          { label: 'Ready to close', text: 'projects whose milestones are all done and paid.' },
          { label: 'Onboarding', text: 'people waiting to be checked.' },
          { label: 'People & Access', text: 'everyone, their roles and their extra powers.' },
          { label: 'Archive & Retention', text: 'archived projects, legal holds, and what is waiting to be archived.' },
        ],
      },
      {
        title: 'The onboarding pipeline',
        lead: 'Onboarding: every consultant, by how far they have got.',
        img: 'adm-pipeline.webp',
        alt: 'Onboarding Pipeline with columns from Applied to Suspended',
        points: [
          { label: 'Applied', text: 'signed up, nothing checked yet.' },
          { label: 'Under Review', text: 'some steps done.' },
          { text: 'Click a person to check them.' },
          { label: 'Invite Team Member', text: 'brings somebody in by email.' },
        ],
      },
      {
        title: 'Verify and activate',
        lead: 'The person\'s checklist opens below the columns.',
        img: 'adm-verify.webp',
        alt: 'Onboarding checklist with Mark Verified and Activate Consultant',
        points: [
          { label: 'Onboarding Checklist', text: 'identity documents, personal details check and consent.' },
          { label: 'Mark Verified', text: 'on each item once you have checked it.' },
          { label: 'Activate Consultant', text: 'when every item is verified. They can then be offered work.' },
        ],
      },
      {
        title: 'Counter-sign a contract',
        lead: 'Contracts, open one marked Pending Signature. The consultant has signed; you sign second.',
        img: 'adm-countersign.webp',
        alt: 'Counter-Sign the Contract: agree, type your name, Counter-Sign (Make Active)',
        points: [
          { text: 'The signature workflow shows that step 3 is yours.' },
          { text: 'Tick to agree to the terms.' },
          { text: 'Type your full name.' },
          { label: 'Counter-Sign (Make Active)', text: 'the contract is active and the consultant can submit work.' },
        ],
      },
      {
        title: 'Approve an invoice',
        lead: 'Project, Deliverables tab. Every invoice needs the PM\'s approval and yours, in either order.',
        img: 'adm-invoice.webp',
        alt: 'Milestone marked Approve invoice, with View Invoice and Approve Invoice buttons',
        points: [
          { label: 'Approve invoice', text: 'marks a milestone waiting for approval.' },
          { label: 'View Invoice', text: 'shows the invoice and any bills on it.' },
          { label: 'Approve Invoice → Release to Finance', text: 'gives your approval. When both are in, Finance can pay.' },
        ],
      },
      {
        title: 'Confirm a closure',
        lead: 'When a PM asks to close a project it shows Pending Closure. Open the project, then Closure.',
        img: 'adm-close.webp',
        alt: 'Project Closure page for a project pending closure',
        points: [
          { label: 'Pending Closure', text: 'the PM has asked to close it.' },
          { label: 'Confirm & Close Project', text: 'closes it, and its active contracts end.' },
          { label: 'Place a legal hold', text: 'stops anything in the project from being deleted, whatever its dates say.' },
        ],
      },
      {
        title: 'Archive a closed project',
        lead: 'Archiving makes the project read-only for long-term keeping. It needs the Terms of Reference on file.',
        img: 'adm-archive.webp',
        alt: 'Archive Project with the Terms of Reference upload',
        points: [
          { label: 'Choose File', text: 'pick the Terms of Reference document.' },
          { label: 'Upload TOR', text: 'saves it to the project.' },
          { label: 'Archive Project', text: 'archives it.' },
        ],
      },
      {
        title: 'People and access',
        lead: 'People & Access: everyone, their role, and what extra they may do.',
        img: 'adm-people.webp',
        alt: 'People & Access list with role, Master PM and Access buttons',
        points: [
          { text: 'Show one role at a time.' },
          { text: 'Change a person\'s role.' },
          { label: 'Make Master PM', text: 'a Project Manager who sees every project, not only their own.' },
          { label: 'Access', text: 'give this person extra powers on top of their role.' },
        ],
      },
      {
        title: 'Give extra powers',
        lead: 'Each power is a switch. The person then sees the matching menus.',
        img: 'adm-powers.webp',
        alt: 'Access window with presets and an On/Off switch for each power',
        points: [
          { label: 'Presets', text: 'for Project Managers, a standard set in one click.' },
          { text: 'Turn a single power On or Off, for example Approve invoices.' },
        ],
      },
      {
        title: 'Archive & Retention',
        lead: 'Every archived project, what is on hold, and what is still waiting to be archived.',
        img: 'adm-retention.webp',
        alt: 'Archive & retention page with counts and a project waiting to be archived',
        points: [
          { text: 'The counts: on legal hold, past retention, ending soon, kept.' },
          { label: 'Waiting to be archived', text: 'closed projects not archived yet. You are reminded weekly once one has waited 30 days.' },
          { text: 'What is missing before it can be archived.' },
        ],
      },
    ],
  },
]
